/**
 * A lead, as a contact in somebody's Google account.
 *
 * Jonathon, 2026-09-10: "samuel has an android" — he wanted Samuel to have the
 * same thing ADR 0108 gave his iPhone, every lead in the phone's address book.
 * The iPhone route runs through a Mac and iCloud, and an Android phone has
 * neither. What it does have is a Google account, which Samuel already
 * connects to Roof Board to send mail, and which is where an Android phone
 * keeps its contacts. So Roof Board writes the cards straight into that
 * account through Google's People API and the phone picks them up itself —
 * no Mac, no script, nothing installed. ADR 0128.
 *
 * The cards are the same cards: `phoneContactCards` decides what a contact
 * says, once, for both routes. This file is only the translation into Google's
 * shape and the decision about what to do with each card — create it, join a
 * contact that is already there, update one we wrote before, or leave it
 * alone. No network and no Supabase; the Edge Function sends what this plans.
 *
 * The rules are the Mac script's rules, because Jonathon asked for them there
 * and they are as true in a Google account:
 *
 *   - **Never a second copy of somebody already in the phone.** A card with no
 *     link and no marker is matched on the last ten digits of a number, or on
 *     the email, against every contact already in the account. One match is
 *     joined. Two is a couple, and is reported rather than guessed.
 *   - **Never overwrite what the person wrote.** On every contact, only the
 *     values this sync last wrote are taken back out; a number somebody added
 *     by hand stays. The name is only rewritten while it still says exactly
 *     what Roof Board last put there. The note keeps whatever they had in it,
 *     with ours underneath.
 *   - **Never delete.** And, one rule this route adds: a contact the person
 *     deleted on their phone is not put back. The Mac has no way to tell a
 *     deleted card from one it never made; Google does, because the link says
 *     which contact we made, and a missing one was removed on purpose.
 */

import {
  CONTACT_MARKER_PREFIX,
  markerContactId,
  phoneMatchKey,
  type PhoneContactCard,
} from "./phoneContacts.js";

/**
 * Read and write the account's contacts.
 *
 * Google offers nothing narrower that can write — `contacts.readonly` cannot
 * create anything. Asked for only from the people who hold
 * `profiles.syncs_google_contacts`, so nobody else's connection ever carries
 * it.
 */
export const GOOGLE_CONTACTS_SCOPE = "https://www.googleapis.com/auth/contacts";

/** What is read back about every contact already in the account. */
export const GOOGLE_READ_FIELDS =
  "names,organizations,phoneNumbers,emailAddresses,addresses,urls,biographies,metadata";

/**
 * What an update may touch, when it is allowed to rename.
 *
 * An update mask applies to a whole batch, and a field in the mask that the
 * person does not carry is *cleared*. So a contact whose name must be left
 * alone goes in a batch whose mask leaves names out altogether — see
 * `GOOGLE_UPDATE_FIELDS_KEEPING_NAME` — rather than having its name read back
 * and re-sent, which is how a name nobody touched gets flattened.
 */
export const GOOGLE_UPDATE_FIELDS =
  "names,organizations,phoneNumbers,emailAddresses,addresses,urls,biographies";

/** The same, for a contact whose name is somebody else's. */
export const GOOGLE_UPDATE_FIELDS_KEEPING_NAME =
  "phoneNumbers,emailAddresses,addresses,urls,biographies";

/** Google's ceiling on one batch create or update. */
export const GOOGLE_BATCH_LIMIT = 200;

/** The labels on the two links, so they can be told apart on the phone. */
export const GOOGLE_URL_TYPES = {
  roofBoard: "Roof Board",
  companycam: "CompanyCam",
} as const;

export interface GoogleName {
  givenName?: string;
  familyName?: string;
  unstructuredName?: string;
  displayName?: string;
}

export interface GoogleOrganization {
  name?: string;
  title?: string;
  department?: string;
}

export interface GoogleValue {
  value?: string;
  type?: string;
}

export interface GoogleAddress {
  streetAddress?: string;
  city?: string;
  region?: string;
  postalCode?: string;
  country?: string;
  type?: string;
}

export interface GoogleBiography {
  value?: string;
  contentType?: string;
}

/** A Google contact, as much of it as this sync reads or writes. */
export interface GooglePerson {
  resourceName?: string | undefined;
  etag?: string;
  names?: GoogleName[];
  organizations?: GoogleOrganization[];
  phoneNumbers?: GoogleValue[];
  emailAddresses?: GoogleValue[];
  addresses?: GoogleAddress[];
  urls?: GoogleValue[];
  biographies?: GoogleBiography[];
}

/** Which Google contact a lead became, and what was last written to it. */
export interface GoogleContactLink {
  contact_id: string;
  resource_name: string;
  revision: string;
  /** The card as last written. How an update knows which values are ours. */
  card: PhoneContactCard | null;
  /** Set once the person deleted the contact. It is not put back. */
  gone_at?: string | null;
}

export interface GoogleUpdate {
  card: PhoneContactCard;
  person: GooglePerson;
  /** A contact that was already in the account, now carrying the lead. */
  joined: boolean;
  /** Whether the name may be written. Decides which batch it goes in. */
  rewritesName: boolean;
}

export interface GooglePlan {
  creates: { card: PhoneContactCard; person: GooglePerson }[];
  updates: GoogleUpdate[];
  unchanged: PhoneContactCard[];
  /** Linked, and deleted by the person since. Left deleted. */
  gone: PhoneContactCard[];
  /** Not written, with the sentence saying why. */
  skipped: { card: PhoneContactCard; reason: string }[];
}

// ------------------------------------------------------------- the names ---

/** The name a card would put on a contact, as a phone would show it. */
export function cardDisplayName(card: PhoneContactCard | null): string {
  if (!card) return "";
  if (card.isCompany) return (card.organization ?? "").trim();
  return [card.first, card.last].filter(Boolean).join(" ").trim();
}

/** The name a Google contact shows, however it was entered. */
export function personDisplayName(person: GooglePerson): string {
  const name = person.names?.[0];
  const spoken =
    name?.displayName ??
    name?.unstructuredName ??
    [name?.givenName, name?.familyName].filter(Boolean).join(" ");
  const trimmed = (spoken ?? "").trim();
  if (trimmed) return trimmed;
  return (person.organizations?.[0]?.name ?? "").trim();
}

/**
 * May this sync write the contact's name?
 *
 * Only while the contact has no name, or still carries exactly the name Roof
 * Board last wrote. A joined contact has no previous card, so a name of its
 * own always survives — which is the point: "Bill Baker (roofer)" is a name
 * somebody chose.
 */
export function mayRename(
  person: GooglePerson,
  previous: PhoneContactCard | null,
): boolean {
  const current = personDisplayName(person);
  return current === "" || current === cardDisplayName(previous);
}

// -------------------------------------------------------------- the note ---

/** True for a line this sync wrote, so it is replaced rather than stacked. */
export function isOurNoteLine(line: string): boolean {
  const trimmed = line.trim();
  return (
    trimmed.startsWith(CONTACT_MARKER_PREFIX) ||
    trimmed.startsWith("Lead source: ") ||
    /^Job \d+$/.test(trimmed)
  );
}

/**
 * Our lines, under whatever somebody else had written.
 *
 * A joined contact's note usually says something worth keeping — a gate code,
 * a dog. Replacing it loses that; appending on every run stacks the same three
 * lines for ever. So our previous lines come out and the current ones go back.
 */
export function mergedNote(
  existing: string | null | undefined,
  note: string,
): string {
  const kept = (existing ?? "")
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .filter((line) => line.trim() !== "" && !isOurNoteLine(line))
    .map((line) => line.trimEnd());
  return kept.length > 0 ? `${kept.join("\n")}\n\n${note}` : note;
}

// ---------------------------------------------------------- the contacts ---

/** Every key a Google contact can be recognised by. */
export function personMatchKeys(person: GooglePerson): string[] {
  const keys = new Set<string>();
  for (const phone of person.phoneNumbers ?? []) {
    const key = phoneMatchKey(phone.value);
    if (key) keys.add(key);
  }
  for (const email of person.emailAddresses ?? []) {
    const value = (email.value ?? "").trim().toLowerCase();
    if (value) keys.add(value);
  }
  return [...keys].sort();
}

/** Only the named string fields, and only the ones with something in them. */
function pick<T extends object>(item: T, keys: readonly (keyof T)[]): T {
  const out = {} as T;
  for (const key of keys) {
    const value = item[key];
    if (typeof value === "string" && value.trim() !== "") out[key] = value;
  }
  return out;
}

const VALUE_KEYS = ["value", "type"] as const;
const ADDRESS_KEYS = [
  "streetAddress",
  "city",
  "region",
  "postalCode",
  "country",
  "type",
] as const;
const ORG_KEYS = ["name", "title", "department"] as const;

const lower = (value: string | null | undefined) =>
  (value ?? "").trim().toLowerCase();

/**
 * The contact as it should be after this card is written to it.
 *
 * `existing` is what is in the account now — an empty person for a contact
 * that does not exist yet — and `previous` is the card last written, or null.
 * Every list keeps what was there except the values we wrote before or are
 * about to write, then gets ours. Matching numbers on their last ten digits
 * rather than as strings, because Google hands back `(251) 555-0147` for a
 * number sent as `251-555-0147`, and an exact comparison would leave the old
 * one behind next to the new.
 */
export function desiredPerson(
  existing: GooglePerson,
  card: PhoneContactCard,
  previous: PhoneContactCard | null,
  rewritesName: boolean,
): GooglePerson {
  const both = [previous, card].filter(Boolean) as PhoneContactCard[];
  const person: GooglePerson = {};
  if (existing.etag) person.etag = existing.etag;

  if (rewritesName) {
    person.names = card.isCompany
      ? []
      : [
          pick({ givenName: card.first, familyName: card.last }, [
            "givenName",
            "familyName",
          ]),
        ];
    const ourOrgs = new Set(
      both.map((c) => lower(c.organization)).filter(Boolean),
    );
    person.organizations = (existing.organizations ?? [])
      .filter((org) => !ourOrgs.has(lower(org.name)))
      .map((org) => pick(org, ORG_KEYS));
    if (card.isCompany && card.organization) {
      person.organizations.push({ name: card.organization });
    }
  }

  const ourPhones = new Set(
    both
      .flatMap((c) => [c.mobile, c.phone])
      .map((value) => phoneMatchKey(value))
      .filter((key): key is string => key !== null),
  );
  person.phoneNumbers = (existing.phoneNumbers ?? [])
    .filter((phone) => {
      const key = phoneMatchKey(phone.value);
      return key === null || !ourPhones.has(key);
    })
    .map((phone) => pick(phone, VALUE_KEYS));
  if (card.mobile)
    person.phoneNumbers.push({ value: card.mobile, type: "mobile" });
  if (card.phone) person.phoneNumbers.push({ value: card.phone, type: "home" });

  const ourEmails = new Set(both.map((c) => lower(c.email)).filter(Boolean));
  person.emailAddresses = (existing.emailAddresses ?? [])
    .filter((email) => !ourEmails.has(lower(email.value)))
    .map((email) => pick(email, VALUE_KEYS));
  if (card.email)
    person.emailAddresses.push({ value: card.email, type: "home" });

  const ourStreets = new Set(both.map((c) => lower(c.street)).filter(Boolean));
  person.addresses = (existing.addresses ?? [])
    .filter((address) => !ourStreets.has(lower(address.streetAddress)))
    .map((address) => pick(address, ADDRESS_KEYS));
  if (card.street) {
    person.addresses.push(
      pick(
        {
          streetAddress: card.street,
          city: card.city ?? "",
          region: card.state ?? "",
          postalCode: card.zip ?? "",
          country: "United States",
          type: "home",
        },
        ADDRESS_KEYS,
      ),
    );
  }

  const ourUrls = new Set(
    both
      .flatMap((c) => [c.roofBoardUrl, c.companycamUrl])
      .map((value) => (value ?? "").trim())
      .filter(Boolean),
  );
  person.urls = (existing.urls ?? [])
    .filter((url) => !ourUrls.has((url.value ?? "").trim()))
    .map((url) => pick(url, VALUE_KEYS));
  person.urls.push({
    value: card.roofBoardUrl,
    type: GOOGLE_URL_TYPES.roofBoard,
  });
  if (card.companycamUrl) {
    person.urls.push({
      value: card.companycamUrl,
      type: GOOGLE_URL_TYPES.companycam,
    });
  }

  person.biographies = [
    {
      value: mergedNote(existing.biographies?.[0]?.value, card.note),
      contentType: "TEXT_PLAIN",
    },
  ];

  return person;
}

/** A brand-new contact for a card. */
export function newGooglePerson(card: PhoneContactCard): GooglePerson {
  return desiredPerson({}, card, null, true);
}

// -------------------------------------------------------------- the plan ---

/**
 * What to do with every card, given what is already in the account.
 *
 * In order of trust: the link this sync stored when it made the contact; then
 * the marker in a contact's note, which survives a lost link; then the numbers
 * and the email, against contacts nothing has claimed. A contact already
 * carrying another lead is never matched to this one, and no contact is joined
 * to two leads in one run.
 */
export function planGoogleContactSync(input: {
  cards: readonly PhoneContactCard[];
  links: readonly GoogleContactLink[];
  people: readonly GooglePerson[];
}): GooglePlan {
  const plan: GooglePlan = {
    creates: [],
    updates: [],
    unchanged: [],
    gone: [],
    skipped: [],
  };

  const byResource = new Map<string, GooglePerson>();
  for (const person of input.people) {
    if (person.resourceName) byResource.set(person.resourceName, person);
  }

  const linkFor = new Map<string, GoogleContactLink>();
  const linked = new Set<string>();
  for (const link of input.links) {
    linkFor.set(link.contact_id, link);
    linked.add(link.resource_name);
  }

  const byMarker = new Map<string, GooglePerson>();
  const strangers = new Map<string, string[]>();
  for (const person of input.people) {
    if (!person.resourceName) continue;
    const marked = markerContactId(person.biographies?.[0]?.value);
    if (marked) {
      if (!byMarker.has(marked)) byMarker.set(marked, person);
      continue;
    }
    if (linked.has(person.resourceName)) continue;
    for (const key of personMatchKeys(person)) {
      const list = strangers.get(key) ?? [];
      list.push(person.resourceName);
      strangers.set(key, list);
    }
  }

  const claimed = new Set<string>();
  const update = (
    card: PhoneContactCard,
    person: GooglePerson,
    previous: PhoneContactCard | null,
    joined: boolean,
  ) => {
    const rewritesName = mayRename(person, previous);
    claimed.add(person.resourceName!);
    plan.updates.push({
      card,
      person: {
        resourceName: person.resourceName,
        ...desiredPerson(person, card, previous, rewritesName),
      },
      joined,
      rewritesName,
    });
  };

  for (const card of input.cards) {
    const link = linkFor.get(card.id);

    if (link?.gone_at) {
      plan.gone.push(card);
      continue;
    }

    if (link) {
      const person =
        byResource.get(link.resource_name) ?? byMarker.get(card.id) ?? null;
      if (!person || claimed.has(person.resourceName!)) {
        plan.gone.push(card);
        continue;
      }
      if (
        link.revision === card.revision &&
        person.resourceName === link.resource_name
      ) {
        claimed.add(person.resourceName!);
        plan.unchanged.push(card);
        continue;
      }
      update(card, person, link.card, false);
      continue;
    }

    const marked = byMarker.get(card.id);
    if (marked && !claimed.has(marked.resourceName!)) {
      update(card, marked, null, false);
      continue;
    }

    const hits: string[] = [];
    for (const key of card.matchKeys) {
      for (const resource of strangers.get(key) ?? []) {
        if (!claimed.has(resource) && !hits.includes(resource)) {
          hits.push(resource);
        }
      }
    }

    if (hits.length > 1) {
      plan.skipped.push({
        card,
        reason: "matches more than one contact already in your phone",
      });
      continue;
    }
    if (hits.length === 1) {
      update(card, byResource.get(hits[0]!)!, null, true);
      continue;
    }

    plan.creates.push({ card, person: newGooglePerson(card) });
  }

  return plan;
}

/**
 * Leads that are the same person twice in Roof Board.
 *
 * Two contact rows holding one number become two contacts, and no matching in
 * the phone fixes that — the duplicate is upstream. Named so it can be merged
 * where it actually is.
 */
export function leadsSharingAKey(
  cards: readonly PhoneContactCard[],
): { key: string; names: string[] }[] {
  const byKey = new Map<string, PhoneContactCard[]>();
  for (const card of cards) {
    for (const key of card.matchKeys) {
      const list = byKey.get(key) ?? [];
      list.push(card);
      byKey.set(key, list);
    }
  }
  const clashes: { key: string; names: string[] }[] = [];
  for (const [key, group] of [...byKey.entries()].sort()) {
    if (new Set(group.map((card) => card.id)).size > 1) {
      clashes.push({
        key,
        names: [...new Set(group.map(cardDisplayName))].sort(),
      });
    }
  }
  return clashes;
}
